import { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users2, FileText, Receipt, Package, BarChart3, ScrollText,
  ShieldCheck, LogOut, Menu, X, Ship, Globe2, KeyRound,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";

const NAV = [
  { to: "/panel", label: "Operasyon Paneli", icon: LayoutDashboard, perm: "operation:read", testId: "nav-dashboard" },
  { to: "/cariler", label: "Cari Kayıtlar", icon: Users2, perm: "party:read", testId: "nav-parties" },
  { to: "/talepler", label: "Talepler", icon: FileText, perm: "request:read", testId: "nav-requests" },
  { to: "/teklifler", label: "Teklifler", icon: Receipt, perm: "quote:read", testId: "nav-quotes" },
  { to: "/tarifeler", label: "Tarifeler", icon: Globe2, perm: "quote:read", testId: "nav-tariffs" },
  { to: "/operasyonlar", label: "Operasyonlar", icon: Package, perm: "operation:read", testId: "nav-operations" },
  { to: "/raporlar", label: "Raporlar", icon: BarChart3, perm: "report:read", testId: "nav-reports" },
  { to: "/denetim", label: "Denetim Kayıtları", icon: ScrollText, perm: "audit:read", testId: "nav-audit" },
  { to: "/kullanicilar", label: "Kullanıcılar", icon: ShieldCheck, perm: "*", testId: "nav-users" },
];

export const Layout = () => {
  const { user, logout, can } = useAuth();
  const [open, setOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const nav = useNavigate();
  const items = NAV.filter((n) => can(n.perm));

  const doLogout = async () => {
    await logout();
    nav("/giris");
  };

  return (
    <div className="grain min-h-screen bg-white">
      <aside
        data-testid="sidebar"
        className={`fixed inset-y-0 left-0 z-40 w-64 border-r border-zinc-200 bg-white transition-transform duration-150 lg:translate-x-0 ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex h-16 items-center justify-between border-b border-zinc-950 px-5">
          <Link to="/panel" className="flex items-center gap-2" data-testid="brand-link">
            <Ship className="h-5 w-5 text-klein" strokeWidth={2.5} />
            <span className="font-display text-base font-black tracking-tighter">SEVER<span className="text-klein">GATE</span></span>
          </Link>
          <button className="lg:hidden" onClick={() => setOpen(false)} data-testid="sidebar-close">
            <X className="h-5 w-5" />
          </button>
        </div>
        <nav className="flex flex-col py-4">
          {items.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={n.testId}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 border-l-2 px-5 py-2.5 text-sm transition-colors ${
                  isActive
                    ? "border-klein bg-zinc-50 font-semibold text-zinc-950"
                    : "border-transparent text-zinc-600 hover:bg-zinc-50 hover:text-zinc-950"
                }`
              }
            >
              <n.icon className="h-4 w-4" />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="absolute bottom-0 w-full border-t border-zinc-200 p-4">
          <p className="overline">Oturum</p>
          <p className="mt-1 truncate text-sm font-semibold" data-testid="sidebar-user-name">{user?.name}</p>
          <p className="text-xs uppercase tracking-wider text-klein" data-testid="sidebar-user-role">{user?.role}</p>
          <button onClick={doLogout} className="brut-btn-ghost mt-3 w-full" data-testid="logout-button">
            <LogOut className="h-4 w-4" /> Çıkış
          </button>
          <button onClick={() => setPwOpen(true)} className="brut-btn-ghost mt-2 w-full" data-testid="change-password-button">
            <KeyRound className="h-4 w-4" /> Şifre Değiştir
          </button>
        </div>
      </aside>
      {pwOpen && <ChangePasswordDialog onClose={() => setPwOpen(false)} />}

      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-zinc-200 bg-white/90 px-4 backdrop-blur sm:px-6">
          <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="sidebar-open">
            <Menu className="h-5 w-5" />
          </button>
          <p className="overline">Severgate Logistics · Operasyon ve Lojistik Yönetim Sistemi</p>
        </header>
        <main className="relative z-10 p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout;
